import React, { useEffect, useRef, useState } from 'react';

export interface FilterSelectOption {
  value: string;
  label: string;
}

interface FilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: FilterSelectOption[];
  className?: string;
  'data-testid'?: string;
  searchable?: boolean;
}

/**
 * Compact single-select pill dropdown for filter rows — mirrors the
 * trigger/menu pattern of AnalysisProfileSelect.tsx (app SVG chevron that
 * rotates when open, absolute bg-surface/border-line menu) but without
 * search, and sized to its own label (inline-block) rather than stretching
 * to the widest option like a native <select>. The trigger is fixed to the
 * app's 42px row-control standard (see _config/conventions.md).
 */
export const FilterSelect: React.FC<FilterSelectProps> = ({
  value,
  onChange,
  options,
  className = '',
  'data-testid': dataTestId,
  searchable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  const selected = options.find((option) => option.value === value);

  const visibleOptions = searchable && searchTerm
    ? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} className={`relative inline-block ${className}`}>
      <div
        data-testid={dataTestId}
        className={`inline-flex items-center gap-2 cursor-pointer rounded-full border bg-surface h-[42px] px-3.5 text-sm text-ink hover:border-accent/40 transition-all ${
          isOpen ? 'border-accent ring-1 ring-accent' : 'border-line'
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="whitespace-nowrap">{selected ? selected.label : ''}</span>
        <svg
          className={`w-5 h-5 text-ink-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div
          className="absolute z-50 mt-1 min-w-full w-max bg-surface border border-line rounded-lg shadow-lg"
          style={{ maxHeight: '15rem', overflowY: 'auto', overscrollBehavior: 'contain' }}
        >
          {searchable && (
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full outline-none bg-surface text-ink px-3.5 py-2 text-sm border-b border-line sticky top-0"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          )}
          {visibleOptions.map((option) => (
            <div
              key={option.value}
              className={`px-3.5 py-2 text-sm cursor-pointer whitespace-nowrap hover:bg-ghost-bg ${
                option.value === value ? 'bg-nav-active text-accent-active' : 'text-ink'
              }`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterSelect;
